import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useStructuraAuth } from '@structura/auth'
import { useTranslation, TranslationKey } from '@structura/i18n'
import { beamRouteForMaterial } from '@structura/services'
import { BEAM_TEMPLATE_STORAGE_KEY, BeamTemplateAction, BeamTemplateData, BeamTemplateSection, BeamTemplateTimberMaterial, SMBeamNode } from '@structura/beam-analysis'
import '@structura/ui/Dashboard.css'

interface BeamTemplate {
  id: string
  nameKey: TranslationKey
  descriptionKey: TranslationKey
  icon: string
  material: 'concrete' | 'steel' | 'timber'
  timberKind?: 'glulam' | 'solid'
  materialLabel: string
  spans: number
  spanLengths: number[]
  loadKey: TranslationKey
  context: string
  accent: string
  section: BeamTemplateSection
  timberMaterial: BeamTemplateTimberMaterial
  supportTypes: SMBeamNode['supportType'][]
  bearingLengthsMm: Array<number | undefined>
  actions: Array<Omit<BeamTemplateAction, 'name'> & { nameKey: TranslationKey }>
}

const timberTemplates: BeamTemplate[] = [
  {
    id: 'timber-roof-simple',
    nameKey: 'beam.dashboard.tpl.timber-roof-simple.name',
    descriptionKey: 'beam.dashboard.tpl.timber-roof-simple.description',
    icon: 'bi-house',
    material: 'timber',
    timberKind: 'glulam',
    materialLabel: 'GL30c · 215×765',
    spans: 1,
    spanLengths: [10],
    loadKey: 'beam.dashboard.tpl.timber-roof-simple.load',
    context: 'roof',
    accent: 'goldenrod',
    section: { name: 'GL30c 215×765', key: 'GLULAM_215x765', width: 215, height: 765 },
    timberMaterial: { kind: 'glulam', timberClass: 'GL30c', elasticModulus: 13e9, density: 430, loadDuration: 'medium-term', serviceClass: 2, checkLateralTorsionalBuckling: false },
    supportTypes: ['Pinned', 'Roller'],
    bearingLengthsMm: [240, 240],
    actions: [
      { id: 'roof-build-up', nameKey: 'beam.dashboard.load.roofBuildUp', category: 'Permanent', spanIntensities: [-1.8] },
      { id: 'snow', nameKey: 'beam.dashboard.load.snow', category: 'Snow', spanIntensities: [-6.0] },
      { id: 'wind', nameKey: 'beam.dashboard.load.windPressure', category: 'Wind', spanIntensities: [-1.5] }
    ]
  },
  {
    id: 'timber-roof-overhang',
    nameKey: 'beam.dashboard.tpl.timber-roof-overhang.name',
    descriptionKey: 'beam.dashboard.tpl.timber-roof-overhang.description',
    icon: 'bi-snow2',
    material: 'timber',
    timberKind: 'glulam',
    materialLabel: 'GL30c · 190×630',
    spans: 2,
    spanLengths: [8, 1.5],
    loadKey: 'beam.dashboard.tpl.timber-roof-overhang.load',
    context: 'roof',
    accent: 'goldenrod',
    section: { name: 'GL30c 190×630', key: 'GLULAM_190x630', width: 190, height: 630 },
    timberMaterial: { kind: 'glulam', timberClass: 'GL30c', elasticModulus: 13e9, density: 430, loadDuration: 'medium-term', serviceClass: 2, checkLateralTorsionalBuckling: false },
    supportTypes: ['Pinned', 'Roller', 'Free'],
    bearingLengthsMm: [200, 200, undefined],
    actions: [
      { id: 'roof-build-up', nameKey: 'beam.dashboard.load.roofBuildUp', category: 'Permanent', spanIntensities: [-1.5, -1.5] },
      { id: 'snow', nameKey: 'beam.dashboard.load.snowWithDrift', category: 'Snow', spanIntensities: [-4.5, -7.0] },
      { id: 'wind', nameKey: 'beam.dashboard.load.windPressure', category: 'Wind', spanIntensities: [-1.2, -1.2] }
    ]
  },
  {
    id: 'timber-floor-multispan',
    nameKey: 'beam.dashboard.tpl.timber-floor-multispan.name',
    descriptionKey: 'beam.dashboard.tpl.timber-floor-multispan.description',
    icon: 'bi-grid-3x2',
    material: 'timber',
    timberKind: 'solid',
    materialLabel: 'C24 · 73×223',
    spans: 3,
    spanLengths: [4, 4, 4],
    loadKey: 'beam.dashboard.tpl.timber-floor-multispan.load',
    context: 'residential',
    accent: 'goldenrod',
    section: { name: 'C24 73×223', key: 'TIMBER_73x223', width: 73, height: 223 },
    timberMaterial: { kind: 'solid', timberClass: 'C24', elasticModulus: 11e9, density: 420, loadDuration: 'medium-term', serviceClass: 1, checkLateralTorsionalBuckling: false },
    supportTypes: ['Pinned', 'Roller', 'Roller', 'Roller'],
    bearingLengthsMm: [120, 120, 120, 120],
    actions: [
      { id: 'floor-build-up', nameKey: 'beam.dashboard.load.floorBuildUp', category: 'Permanent', spanIntensities: [-0.45, -0.45, -0.45] },
      { id: 'residential', nameKey: 'beam.dashboard.load.residential', category: 'Variable', spanIntensities: [-1.2, -1.2, -1.2] }
    ]
  },
  {
    id: 'timber-floor-joist',
    nameKey: 'beam.dashboard.tpl.timber-floor-joist.name',
    descriptionKey: 'beam.dashboard.tpl.timber-floor-joist.description',
    icon: 'bi-distribute-horizontal',
    material: 'timber',
    timberKind: 'solid',
    materialLabel: 'C24 · 48×223',
    spans: 1,
    spanLengths: [3.6],
    loadKey: 'beam.dashboard.tpl.timber-floor-joist.load',
    context: 'residential',
    accent: 'goldenrod',
    section: { name: 'C24 48×223', key: 'TIMBER_48x223', width: 48, height: 223 },
    timberMaterial: { kind: 'solid', timberClass: 'C24', elasticModulus: 11e9, density: 420, loadDuration: 'medium-term', serviceClass: 1, checkLateralTorsionalBuckling: false },
    supportTypes: ['Pinned', 'Roller'],
    bearingLengthsMm: [90, 90],
    actions: [
      { id: 'floor-build-up', nameKey: 'beam.dashboard.load.floorBuildUp', category: 'Permanent', spanIntensities: [-0.45] },
      { id: 'residential', nameKey: 'beam.dashboard.load.residential', category: 'Variable', spanIntensities: [-1.2] }
    ]
  },
  {
    id: 'timber-carport',
    nameKey: 'beam.dashboard.tpl.timber-carport.name',
    descriptionKey: 'beam.dashboard.tpl.timber-carport.description',
    icon: 'bi-car-front-fill',
    material: 'timber',
    timberKind: 'glulam',
    materialLabel: 'GL30c · 165×495',
    spans: 3,
    spanLengths: [1, 6, 1],
    loadKey: 'beam.dashboard.tpl.timber-carport.load',
    context: 'roof',
    accent: 'goldenrod',
    section: { name: 'GL30c 165×495', key: 'GLULAM_165x495', width: 165, height: 495 },
    timberMaterial: { kind: 'glulam', timberClass: 'GL30c', elasticModulus: 13e9, density: 430, loadDuration: 'medium-term', serviceClass: 2, checkLateralTorsionalBuckling: false },
    supportTypes: ['Free', 'Pinned', 'Roller', 'Free'],
    bearingLengthsMm: [undefined, 180, 180, undefined],
    actions: [
      { id: 'roof-build-up', nameKey: 'beam.dashboard.load.lightRoofBuildUp', category: 'Permanent', spanIntensities: [-1.4, -1.4, -1.4] },
      { id: 'snow', nameKey: 'beam.dashboard.load.snow', category: 'Snow', spanIntensities: [-4.0, -4.0, -4.0] },
      { id: 'wind', nameKey: 'beam.dashboard.load.windPressure', category: 'Wind', spanIntensities: [-1.0, -1.0, -1.0] }
    ]
  },
  {
    id: 'timber-ridge-beam',
    nameKey: 'beam.dashboard.tpl.timber-ridge-beam.name',
    descriptionKey: 'beam.dashboard.tpl.timber-ridge-beam.description',
    icon: 'bi-house-up',
    material: 'timber',
    timberKind: 'glulam',
    materialLabel: 'GL30c · 165×585',
    spans: 1,
    spanLengths: [7.5],
    loadKey: 'beam.dashboard.tpl.timber-ridge-beam.load',
    context: 'roof',
    accent: 'goldenrod',
    section: { name: 'GL30c 165×585', key: 'GLULAM_165x585', width: 165, height: 585 },
    timberMaterial: { kind: 'glulam', timberClass: 'GL30c', elasticModulus: 13e9, density: 430, loadDuration: 'medium-term', serviceClass: 2, checkLateralTorsionalBuckling: false },
    supportTypes: ['Pinned', 'Roller'],
    bearingLengthsMm: [200, 200],
    actions: [
      { id: 'roof-build-up', nameKey: 'beam.dashboard.load.roofBuildUp', category: 'Permanent', spanIntensities: [-2.0] },
      { id: 'snow', nameKey: 'beam.dashboard.load.snow', category: 'Snow', spanIntensities: [-6.0] },
      { id: 'wind', nameKey: 'beam.dashboard.load.windPressure', category: 'Wind', spanIntensities: [-1.5] }
    ]
  }
]

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const authState = useStructuraAuth()
  const { t } = useTranslation()

  const handleTemplateSelect = (template: BeamTemplate): void => {
    const templateData: BeamTemplateData = {
      templateId: template.id,
      material: template.material,
      timberKind: template.timberKind,
      spanLengths: template.spanLengths,
      context: template.context,
      designName: t(template.nameKey),
      selfWeightName: t('beam.dashboard.load.selfWeight'),
      section: template.section,
      timberMaterial: template.timberMaterial,
      supportTypes: template.supportTypes,
      bearingLengthsMm: template.bearingLengthsMm,
      actions: template.actions.map(action => ({
        id: action.id,
        name: t(action.nameKey),
        category: action.category,
        spanIntensities: action.spanIntensities
      }))
    }
    sessionStorage.setItem(BEAM_TEMPLATE_STORAGE_KEY, JSON.stringify(templateData))
    navigate(beamRouteForMaterial(template.material))
  }

  const handleStartBlank = (): void => {
    sessionStorage.removeItem(BEAM_TEMPLATE_STORAGE_KEY)
    navigate('/beam/wood')
  }

  const greeting = React.useMemo((): string => {
    const hour = new Date().getHours()
    if (hour < 12) {
      return t('beam.dashboard.greetingMorning')
    }
    if (hour < 18) {
      return t('beam.dashboard.greetingAfternoon')
    }
    return t('beam.dashboard.greetingEvening')
  }, [t])

  const firstName = authState.userName?.split(' ')[0] || t('beam.dashboard.defaultName')

  return (
    <div className="dashboard-page">
      <div className="dashboard-hero">
        <div className="container">
          <div className="dashboard-welcome">
            <div className="welcome-badge">
              <i className="bi bi-lightning-charge-fill me-2"></i>
              {t('beam.dashboard.readyToDesign')}
            </div>
            <h1 className="welcome-title">{t('beam.dashboard.welcomeTitle', { greeting, name: firstName })}</h1>
            <p className="welcome-subtitle">
              {t('beam.dashboard.welcomeSubtitle')}
            </p>
          </div>

          <div className="quick-actions">
            <button className="quick-action-btn primary" onClick={handleStartBlank}>
              <div className="action-icon">
                <i className="bi bi-plus-lg"></i>
              </div>
              <div className="action-content">
                <span className="action-title">{t('beam.dashboard.newBlankDesign')}</span>
                <span className="action-desc">{t('beam.dashboard.newBlankDesc')}</span>
              </div>
              <i className="bi bi-arrow-right action-arrow"></i>
            </button>
            <button className="quick-action-btn secondary" onClick={() => navigate('/account')}>
              <div className="action-icon">
                <i className="bi bi-person-badge"></i>
              </div>
              <div className="action-content">
                <span className="action-title">{t('beam.dashboard.myAccount')}</span>
                <span className="action-desc">{t('beam.dashboard.myAccountDesc')}</span>
              </div>
              <i className="bi bi-arrow-right action-arrow"></i>
            </button>
          </div>
        </div>
      </div>

      <section className="templates-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              <i className="bi bi-grid-3x3-gap me-2"></i>
              {t('beam.dashboard.beamTemplates')}
            </h2>
            <p className="section-desc">
              {t('beam.dashboard.beamTemplatesDesc')}
            </p>
          </div>

          {/* Single-material app — render the templates as a responsive tile
              grid, not the legacy 3-column-per-material layout. */}
          <div className="column-header column-timber templates-section-header">
            <i className="bi bi-tree me-2"></i>
            {t('beam.dashboard.materialTimber')}
          </div>
          <div className="templates-grid">
            {timberTemplates.map((template: BeamTemplate) => (
              <TemplateCard key={template.id} template={template} onSelect={handleTemplateSelect} />
            ))}
          </div>
          <p className="section-desc mt-3 mb-0">{t('beam.dashboard.timberTemplateAssumptions')}</p>
        </div>
      </section>

      {/* Benefits section — same canonical content as the anonymous Home page,
          rendered here too so logged-in users see it on /dashboard without
          having to bounce back to /. The previous, duplicated features-grid
          (Advanced Analysis / Visual Results / Professional Reports / Code
          Checking) was retired in favour of this single source of truth. */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <h3 className="fw-semibold text-dark mb-4">{t('beam.dashboard.whyChoose')}</h3>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('beam.dashboard.benefitMultiSpanLabel')}</strong> {t('beam.dashboard.benefitMultiSpanDesc')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('beam.dashboard.benefitEurocodeLabel')}</strong> {t('beam.dashboard.benefitEurocodeDesc')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('beam.dashboard.benefitVisualizationLabel')}</strong> {t('beam.dashboard.benefitVisualizationDesc')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('beam.dashboard.benefitResultsLabel')}</strong> {t('beam.dashboard.benefitResultsDesc')}
                </li>
              </ul>
            </div>
            <div className="col-lg-6">
              <h3 className="fw-semibold text-dark mb-4">{t('beam.dashboard.technicalFeatures')}</h3>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <i className="bi bi-gear-fill home-icon me-2"></i>
                  {t('beam.dashboard.featureLoadTypes')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-graph-up home-icon me-2"></i>
                  {t('beam.dashboard.featureDiagrams')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-diagram-2-fill home-icon me-2"></i>
                  {t('beam.dashboard.featureReactions')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-file-earmark-text home-icon me-2"></i>
                  {t('beam.dashboard.featureLoadCases')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

interface TemplateCardProps {
  template: BeamTemplate
  onSelect: (template: BeamTemplate) => void
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onSelect }) => {
  const { t } = useTranslation()
  const spansSpec = t(template.spans > 1 ? 'beam.dashboard.spansPlural' : 'beam.dashboard.spansSingular', { count: template.spans, lengths: template.spanLengths.join(' + ') })
  return (
    <article
      className="template-card"
      style={{ '--template-accent': template.accent } as React.CSSProperties}
      onClick={() => onSelect(template)}
    >
      <div className="template-header">
        <div className="template-icon">
          <i className={`bi ${template.icon}`}></i>
        </div>
        <span className={`material-badge material-${template.material}`}>
          {template.materialLabel}
          {template.timberKind === 'solid' && t('beam.dashboard.solidSuffix')}
          {template.timberKind === 'glulam' && t('beam.dashboard.glulamSuffix')}
        </span>
      </div>
      <h3 className="template-title">{t(template.nameKey)}</h3>
      <p className="template-description">{t(template.descriptionKey)}</p>
      <div className="template-specs">
        <div className="spec-item">
          <i className="bi bi-arrows-expand"></i>
          <span>{spansSpec}</span>
        </div>
        <div className="spec-item">
          <i className="bi bi-arrow-down-circle"></i>
          <span>{t(template.loadKey)}</span>
        </div>
      </div>
      <div className="template-footer">
        <span className="use-template">
          {t('beam.dashboard.useTemplate')}
          <i className="bi bi-arrow-right ms-2"></i>
        </span>
      </div>
    </article>
  )
}

export default Dashboard
